"use client";

import { OVERLAY_BADGE_GEOMETRY } from "@/lib/badge";
import { useBadgePhoneViewport } from "@/hooks/use-badge-phone-viewport";

interface BuiltWithBadgeProps {
  projectId: string;
  /**
   * Extra pixels to lift the badge above its default bottom offset. Used by the
   * mobile workspace preview to clear the collapsed "Open chat" handle, which is
   * pulled up over the preview's bottom edge and would otherwise sit flush
   * against the badge (PANDA — "badge hidden behind build progress / no
   * separation from the chat handle on mobile"). 0 on desktop and wherever no
   * bottom-anchored chrome overlaps the badge.
   */
  bottomInset?: number;
}

/**
 * "Remix with Panda" badge rendered as a UI overlay in the workspace preview.
 * Uses the panda-lockup.png mascot inline between "with" and "Panda".
 *
 * The badge carries NO close/remove affordance, on any tier. It is attribution
 * and a distribution surface, not a dismissible notice: an X on the badge reads
 * as a normal primary action and invites removal during build, preview and
 * publish work, when the product intent is for the badge to stay. Removal is a
 * deliberate, plan-gated setting instead, reached through the nested
 * "Remix with Panda" badge disclosure in project settings (ProjectSettingsModal
 * in app/project/[id]/page.tsx), which is where the capability, its plan gate
 * and its effect on the published app can actually be explained.
 *
 * The lockup is always a single line — it never wraps, stacks or splits. On
 * phones (≤640px) it keeps the same one-line copy and only tightens its own
 * footprint (smaller padding/height/type) so it never sits over the generated
 * app's hero CTA / above-the-fold content or the mobile chat controls
 * (PANDA-1251: "no important content hidden behind Panda UI overlays"). This
 * mirrors the media query baked into the injected `_panda_badge.tsx`
 * (lib/badge.ts) so the workspace preview and the deployed/published build
 * read identically at every width.
 */
export function BuiltWithBadge({
  projectId,
  bottomInset = 0,
}: BuiltWithBadgeProps) {
  const href = `/api/badge-click?project_id=${encodeURIComponent(projectId)}`;
  const isPhone = useBadgePhoneViewport();
  // Offsets + height come from the shared geometry, not from literals here:
  // the demo-limits pill stacks itself above this badge from the same numbers,
  // and a local copy is how the two would drift apart.
  const geometry = isPhone
    ? OVERLAY_BADGE_GEOMETRY.phone
    : OVERLAY_BADGE_GEOMETRY.desktop;

  const iconSize = isPhone ? "18px" : "22px";
  const fontSize = isPhone ? "12px" : "13px";
  const textLineHeight = isPhone ? "18px" : "20px";

  return (
    // The link IS the positioned element. It briefly sat inside a wrapper div
    // because an <a> may not contain interactive content and the remove-× had to
    // be a sibling <button>; with the × gone there is nothing to be a sibling of,
    // so the placement lives back on the badge itself.
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Remix with Panda"
      onClick={(e) => e.stopPropagation()}
      // Mobile: `z-0` so the chat handle (z-10) and chat sheet (z-60)
      // overlay this badge — otherwise the badge floats on top of the
      // chat handle (see screenshot). Desktop: `md:z-40` sits ABOVE every
      // preview overlay (all ≤ z-30 in preview-frame) but BELOW the modal
      // layer (z-[100]). So when a modal is open the badge is dimmed/blurred
      // behind its backdrop like the rest of the page instead of floating on
      // top. (Was md:z-9999, which beat the z-[100] modal backdrop.)
      className="z-0 md:z-40"
      style={{
        position: "absolute",
        // Base offset + any caller-supplied lift (e.g. to clear the collapsed
        // mobile "Open chat" handle so the two don't sit flush).
        bottom: `${geometry.bottom + bottomInset}px`,
        right: `${geometry.right}px`,
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        height: `${geometry.height}px`,
        alignItems: "center",
        gap: isPhone ? "5px" : "6px",
        padding: isPhone ? "0 10px" : "0 14px",
        background: "#0A0A0A",
        borderRadius: "8px",
        textDecoration: "none",
        boxShadow: "0 3px 15px 0 rgba(0, 0, 0, 0.28)",
        cursor: "pointer",
        lineHeight: 1,
        whiteSpace: "nowrap",
        userSelect: "none",
        transition: "opacity 0.15s ease",
        fontFamily: 'var(--font-rubik), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.85"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; }}
    >
      <span
        style={{
          display: "inline-block",
          fontSize,
          fontWeight: 400,
          color: "#9CA3AF",
          lineHeight: textLineHeight,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        Remix with
      </span>

      {/* Panda mascot cropped from the lockup — same trick as components/panda-logo.tsx */}
      <img
        src="/panda-lockup.png"
        alt=""
        aria-hidden
        width={1168}
        height={398}
        style={{
          width: iconSize,
          height: iconSize,
          borderRadius: "50%",
          objectFit: "cover",
          objectPosition: "left",
          display: "block",
          flexShrink: 0,
        }}
      />

      <span
        style={{
          display: "inline-block",
          fontSize,
          fontWeight: 500,
          color: "#D4D4D8",
          lineHeight: textLineHeight,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        Panda
      </span>
    </a>
  );
}
